import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { ExportConfig, ExportOptions, WorkflowStatus } from '@api/projects/interfaces';
import { ProjectService } from '@app/services/project/project.service';
import { ReportConfigWithVisibility } from '@workflow/components/report-card/report-filter.service';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { WorkflowService } from '@workflow/services/workflow.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ParticipantService } from '@app/services/participants/participant.service';
import { InterpolationService } from '@app/services/interpolation/interpolation.service';


export type ActivatableExportConfig = ExportConfig & { active?: boolean };

@Component({
	selector: 'app-select-exports-button',
	templateUrl: './select-exports-button.component.html',
	styleUrls: ['./select-exports-button.component.scss'],
})
export class SelectExportsButtonComponent implements OnChanges, OnInit, OnDestroy {

	@Input() listOfSelectedParticipants: Set<string>;
	@Input() totalNumberOfParticipants = -1;
	@Input() exportConfigs: ActivatableExportConfig[];
	@Input() sourceWindow: 'participantOverview' | 'participantDetails' | 'workflowItemDetail' | 'participantWorkflowOverview' ;
	@Input() reportConfigsinMenu: ReportConfigWithVisibility[];
	@Output() reportConfigToPrint: EventEmitter<string> = new EventEmitter<string>();
	@Input() itemId: string;

	public exportReportSelectedUsersSplitButtons: SplitButtonElement[] = [];
	public exportButtons: SplitButtonElement[];
	public reportButtons: SplitButtonElement[];
	public bulkOperationsButtons: SplitButtonElement[];
	public isResetModalVisible: boolean;
	hasWorkflowWritePermission: boolean;

	private workflowStatus: WorkflowStatus;
	private subscription: Subscription = new Subscription();

	private dateContext: {date: string};
	private dateParticipantContext: {participant: unknown, date: string};
	private documentTitle: string;

	constructor(
		private projectService: ProjectService,
		private workflowService: WorkflowService,
		private translateService: TranslateService,
		private participantService: ParticipantService,
		private interpolationService: InterpolationService,
	) { }

	ngOnInit(): void {
		this.subscription.add(this.workflowService.workflowStatus$.subscribe(status => {
			this.workflowStatus = status;
			this.updateButtons();
		}));
		const date = new Date().toLocaleDateString();
		this.dateContext = {date: date};
		if (this.totalNumberOfParticipants === -1) {
			this.subscription.add(
				this.participantService.getParticipant$(
					this.projectService.activeProject.project_id, 
					this.listOfSelectedParticipants.values().next().value
				).subscribe((iParticipant => {
					this.dateParticipantContext = {participant: iParticipant.data, date: date};
				})));
		}
		this.subscription.add(this.projectService.hasWorkflowWriteOnActiveProject$
			.subscribe(hasWorkflowWritePermission => {
				this.hasWorkflowWritePermission = hasWorkflowWritePermission;
				this.updateButtons();
			}));
	}

	ngOnChanges(): void {
		this.updateButtons();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	@HostListener('window:afterprint', ['$event'])
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onAfterPrint(event: never): void {
		document.title = this.documentTitle;
	}

	public updateButtons(): void {
		const numberSelectedUsers = this.listOfSelectedParticipants.size === 1 && this.totalNumberOfParticipants === -1 ? '' : this.listOfSelectedParticipants.size;

		this.exportButtons = [];
		this.reportButtons = [];
		this.bulkOperationsButtons = [];
		this.exportReportSelectedUsersSplitButtons = [];
		const resetInputsButtons: SplitButtonElement[] = [];
		if (this.reportConfigsinMenu) {
			this.reportButtons = this.reportConfigsinMenu.filter(config => config.printable).map((config) => {
				return {
					title: 'actions.print',
					titleParams: { name: config.label },
					disabled: config.disabled,
					type: 'default',
					icon: 'bar-chart-2',
					callback: () => this.printSelectedConfig(config),
				};
			});
		}
		if (this.exportConfigs) {
			this.exportButtons = this.exportConfigs.map((config, index) => {
				const title = this.totalNumberOfParticipants > 1 && this.totalNumberOfParticipants === this.listOfSelectedParticipants.size ? 'actions.exportAll' : 'actions.export';
				const participantSingularPlural = numberSelectedUsers === 1 ? 'word.participant.singular' : 'word.participant.plural' ;
				return {
					title: numberSelectedUsers === '' ? 'actions.exportOne' : title,
					titleParams: { name: config.label, num: numberSelectedUsers},
					observableTitleParams: { participantNumber: this.translateService.stream(participantSingularPlural) },
					disabled: this.listOfSelectedParticipants.size === 0 || !config.active,
					icon: 'file-text',
					type: 'default',
					callback: () => this.exportSelectedConfig(config),
					hasDivider: this.reportButtons.length > 0 && index === 0,
				};
			});
		}
		if ((this.sourceWindow === 'participantDetails' || this.sourceWindow === 'workflowItemDetail') && this.hasWorkflowWritePermission && this.reportConfigsinMenu?.length) {
			resetInputsButtons.push({
				title: 'workflow.resetReport',
				disabled: !this.reportConfigsinMenu?.some(config => !config.disabled),
				icon: 'rotate-cw',
				type: 'default',
				callback: () => this.resetReports(this.itemId).catch(console.error),
				hasDivider: this.reportButtons.length > 0 || this.exportButtons.length > 0,
			});
		}
		if (this.itemId && this.hasWorkflowWritePermission) {
			resetInputsButtons.push({
				title: 'workflow.resetData',
				disabled: !(this.workflowStatus?.status[this.itemId]?.completed || this.workflowStatus?.status[this.itemId]?.started),
				icon: 'trash',
				type: 'default',
				callback: () => this.showResetModal(),
				hasDivider: (this.reportButtons.length > 0 || this.exportButtons.length > 0) && resetInputsButtons.length === 0,
			});
		}

		this.exportReportSelectedUsersSplitButtons = [...this.reportButtons, ...this.bulkOperationsButtons, ...this.exportButtons, ...resetInputsButtons];

	}

	private exportSelectedConfig(exportConfig: ExportConfig): void {
		if (!exportConfig) {
			return;
		}

		const options: ExportOptions = {
			pids: [...this.listOfSelectedParticipants]
		};
		let fileName;
		if (this.sourceWindow === 'participantOverview') {
			fileName = exportConfig.fileNameTemplate ? this.interpolationService.interpolate(exportConfig.fileNameTemplate, this.dateContext) : null;
		} else {
			fileName = exportConfig.fileNameTemplateWithParticipant ? this.interpolationService.interpolate(exportConfig.fileNameTemplateWithParticipant, this.dateParticipantContext) : null;
		}
		if (!fileName) {
			switch (this.sourceWindow) {
				case 'participantOverview':
					fileName = exportConfig.labelParticipantOverview ?
						exportConfig.labelParticipantOverview : exportConfig.label;
					break;
				case 'participantDetails':
					fileName = exportConfig.labelParticipantDetails ?
						exportConfig.labelParticipantDetails : exportConfig.label;
					break;
				case 'workflowItemDetail':
					fileName = exportConfig.labelWorkflowItemDetail ?
						exportConfig.labelWorkflowItemDetail : exportConfig.label;
					break;
			}
		}
		fileName += '.csv';
		this.projectService.downloadSelectedUserExports(exportConfig, options, fileName);
	}

	private printSelectedConfig(reportConfig: ReportConfigWithVisibility) {
		this.reportConfigToPrint.emit(reportConfig.reportId);
		//Sorgt dafür das erst dann das Druckfenster geöffnet wird nachdem die CSS Klassen angewendet wurden.
		setTimeout(() => {
			if (reportConfig.fileNameTemplate) {
				const filename = this.interpolationService.interpolate(reportConfig.fileNameTemplate, this.dateParticipantContext);
				this.documentTitle = document.title;
				document.title = filename;
			}
			window.print();
			this.reportConfigToPrint.emit(null);
		},0);
	}

	async resetReports(itemId: string): Promise<void> {
		if (itemId) {
			if (!this.workflowStatus?.status[itemId]?.completed) {
				return;
			}
			await this.rerunItemTriggers(itemId).catch(console.error);
		}
		else {
			for (const statusId in this.workflowStatus?.status) {
				if (!this.workflowStatus?.status[statusId]?.completed) {
					continue;
				}
				await this.rerunItemTriggers(statusId).catch(console.error);
			}
		}
	}

	async rerunItemTriggers(itemId: string): Promise<void> {
		const pid = this.listOfSelectedParticipants.values().next().value;
		await this.workflowService.rerunItemTriggers(pid, itemId).catch(console.error);
	}

	showResetModal(): void {
		this.isResetModalVisible = true;
	}

	handleResetCancel(): void {
		this.isResetModalVisible = false;
	}

	async resetData(): Promise<void> {
		this.isResetModalVisible = false;
		await this.workflowService.resetWorkflowItemStatus(this.itemId);
	}
}
